import Button from "components/atoms/Button";
import InputDate from "components/atoms/InputDate";
import InputSelect from "components/atoms/InputSelect";
import TableDataComponent from "components/organisms/TableData";
import moment from "moment";
import React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { MonthOptions, PageMode } from "utils/constant";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import {
  errorToastNotif,
  monthValueConverter,
  setDataToLocalStorage,
  successToastNotif,
} from "utils/common";
import { confirmAlert } from "react-confirm-alert";
import { useEffect } from "react";
import UjiSilangService from "services/UjiSilang";

const ujiSilangService = new UjiSilangService();

const Table = ({ changeMode }) => {
  const [filterState, setFilterState] = useState({
    filter_year: "",
    filter_month: "",
  });
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });
  const [isLoading, setIsLoading] = useState(true);

  const { currentUser } = useSelector((state) => state.authReducer);

  const fetchInputData = async () => {
    setIsLoading(true);
    try {
      const response = await ujiSilangService.getAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy: `fasyankes_id,year,month`,
        searchValue: `${currentUser?.fasyankes_id},${filterState.filter_year},${filterState.filter_month}`,
      });

      setDataState((prevState) => ({
        ...prevState,
        data: response.uji_silang,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInputData();
  }, [dataState.page]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await ujiSilangService
              .remove(id)
              .then((res) => {
                fetchInputData();
                successToastNotif("Data berhasil dihapus");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const onEditData = (item) => {
    setDataToLocalStorage("SELECTED_ITEM", JSON.stringify(item));
    changeMode(PageMode.edit);
  };

  // Data uji silang menjadi read only setelah diverifikasi oleh Kab/Kota,
  // yang ditandai dengan terisinya kolom tglsimpan_kab (Validasi kab/kota).
  const isVerifiedByKabKota = (item) => Boolean(item?.tglsimpan_kab);

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) =>
        isVerifiedByKabKota(row.original) ? "" : (
          <div className="button-actions">
            <ActionButton
              onClick={() => onEditData(row.original)}
              btnType={"edit"}
            />
            <ActionButton
              onClick={() => onRemoveData(row.original.id)}
              btnType={"delete"}
            />
          </div>
        ),
    },
    {
      header: "Validasi kab/kota",
      accessorKey: "tglsimpan_kab",
      cell: ({ row }) => row.original.tglsimpan_kab ?? "",
    },
    {
      header: "Tanggal diinput fasyankes",
      accessorKey: "createdAt",
      cell: ({ row }) => moment(row.original.createdAt).format("YYYY-MM-DD"),
    },
    {
      header: "Provinsi",
      accessorKey: "province_id",
      cell: ({ row }) =>
        row.original.province_id ? row.original.province_id?.split("|")[1] : "",
      size: 250,
    },
    {
      header: "Kabupaten",
      accessorKey: "district_id",
      cell: ({ row }) =>
        row.original.district_id ? row.original.district_id?.split("|")[1] : "",
      size: 250,
    },
    {
      header: "Tahun",
      accessorKey: "year",
      size: 100,
    },
    {
      header: "Bulan",
      accessorKey: "month",
      cell: ({ row }) =>
        row.original.month ? monthValueConverter(row.original.month) : "",
      size: 100,
    },
    {
      header: "Tanggal Sampel Dikirim",
      accessorKey: "tgl_sampel_dikirim",
      cell: ({ row }) => row.original.tgl_sampel_dikirim ?? "",
    },
    {
      header: "Petugas mikroskopis",
      accessorKey: "pjlab",
    },
    {
      header: "Jumlah sedian darah negatif",
      accessorKey: "jumsdneg",
    },
    {
      header: "Jumlah sedian darah positif (mix)",
      accessorKey: "jumsdpos_mix",
      size: 160,
    },
    {
      header: "Jenis Parasit",
      accessorKey: "parasyte",
      cell: ({ row }) =>
        row.original?.parasyte ? row.original?.parasyte?.join(", ") : "",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (pf)",
      accessorKey: "jumsdpos_pf",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (pv)",
      accessorKey: "jumsdpos_pv",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (po)",
      accessorKey: "jumsdpos_po",
      size: 160,
    },
    {
      header: "Jumlah sedian darah positif (pm)",
      accessorKey: "jumsdpos_pm",
      size: 160,
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">Uji Silang Fasyankes</h1>
      {/* Filter section start */}
      <div className="card-filter">
        <div className="filter-item">
          <InputDate
            placeholder={"Pilih tahun"}
            name={"filter_year"}
            dateFormat={"YYYY"}
            onChange={(e) => {
              const selectedYear = moment(e).format("YYYY");
              setFilterState({
                ...filterState,
                filter_year: selectedYear,
              });
            }}
            value={filterState.filter_year}
          />
        </div>
        <div className="filter-item">
          <InputSelect
            placeholder={"Pilih bulan"}
            data={MonthOptions}
            onChange={(e) => {
              const selectedValue = e ? e.value : "";
              setFilterState({
                ...filterState,
                filter_month: selectedValue,
              });
            }}
            value={MonthOptions.find(
              (item) => item.value === filterState.filter_month
            )}
          />
        </div>
        <Button
          variant={"primary"}
          size={"normal"}
          onClick={() => fetchInputData()}
        >
          Terapkan
        </Button>
      </div>
      {/* Filter section end */}

      {/* Actions */}
      <div className="action-wrapper">
        <div className="action-button">
          {currentUser?.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => changeMode(PageMode.add)}
            >
              Tambah data
            </Button>
          )}
        </div>
      </div>

      <TableDataComponent
        columnLength={columns?.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />
    </div>
  );
};

export default Table;
